import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ConceptProgress } from '../PerformanceDrawer/performanceDrawerData';
import { pxToRem } from '@geneo2-web/shared-ui';
import { ProgressBar } from '../ProgressBar';

interface ConceptItemProps {
  data: ConceptProgress;
}

export const ConceptItem = ({ data }: ConceptItemProps) => {
  const { t } = useTranslation();
  const { name, icon, correct, total, status } = data;

  const percent = total > 0 ? (correct / total) * 100 : 0;

  const getBarColor = () => {
    if (status === 'not-started') return '#e2e8f0';
    if (percent >= 70) return '#22c55e';
    if (percent >= 40) return '#f59e0b';
    return '#ef4444';
  };

  const getStyleStatus = (status: string) => {
    switch (status) {
      case 'mastered':
        return statusStyles.mastered;
      case 'struggling':
      case 'beginning':
      case 'developing':
      case 'confident':
        return statusStyles.attempted;
      case 'not_assessed':
        return statusStyles['not-started'];
      default:
        return statusStyles['not-started'];
    }
  };

  const statusStyles = {
    mastered: {
      bg: '#dcfce7',
      color: '#15803d',
      label: 'Mastered',
      labelKey: 'PAL_CI_STATUS_MASTERED',
    },
    attempted: {
      bg: '#fef3c7',
      color: '#92400e',
      label: 'Attempted',
      labelKey: 'PAL_CI_STATUS_ATTEMPTED',
    },
    'not-started': {
      bg: '#f1f5f9',
      color: '#94a3b8',
      label: 'Not Started',
      labelKey: 'PAL_CI_STATUS_NOT_STARTED',
    },
  } as const;

  const style = getStyleStatus(status);

  return (
    <Box display="flex" alignItems="center" gap={pxToRem(10)}>
      {/* ICON */}
      <Typography fontSize={pxToRem(18)}>{icon}</Typography>

      {/* INFO */}
      <Box flex={1} display="flex" flexDirection={'column'} minWidth={0}>
        <Typography
          sx={{
            fontFamily: 'Poppins',
            color: '#1e293b',
            fontSize: pxToRem(13),
          }}
        >
          {name}
        </Typography>
        {total > 0 && (
          <Typography
            sx={{
              color: '#94a3b8',
              fontSize: pxToRem(12),
            }}
          >
            {t('PAL_CI_CORRECT_COUNT', { correct, total })}
          </Typography>
        )}
      </Box>

      {/* BAR */}
      <Box
        width={pxToRem(80)}
        sx={{
          flex: 1,
        }}
      >
        <ProgressBar
          valuePercent={status === 'not-started' ? 0 : percent}
          color={getBarColor()}
          height={6}
        />
      </Box>

      {/* STATUS */}
      <Box
        sx={{
          maxWidth: pxToRem(80),
          fontFamily: 'Lato',
          fontWeight: 700,
          fontSize: pxToRem(10),
          px: pxToRem(8),
          py: pxToRem(2),
          borderRadius: pxToRem(6),
          background: style.bg,
          color: style.color,
          textTransform: 'uppercase',
        }}
      >
        {t(`PAL_KC_STATUS_${status?.toUpperCase() || 'NOT_STARTED'}`)}
      </Box>
    </Box>
  );
};
